import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import Sidebar from "./Navbar";
import { BASE_URL } from "./constants";

const LiveBookings = () => {
  const admin = JSON.parse(localStorage.getItem("admin"));
  const [liveBookings, setLiveBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Filters & Controls
  const [activeFilter, setActiveFilter] = useState("ALL"); // ALL | SEARCHING | ACCEPTED | STARTED
  const [searchQuery, setSearchQuery] = useState("");
  const [autoSync, setAutoSync] = useState(true);

  // Action Modals State
  const [actionModal, setActionModal] = useState({
    isOpen: false,
    type: null, // "FORCE_START" | "FORCE_COMPLETE" | "RE_SEARCH" | "CANCEL"
    booking: null,
    reason: "",
    overridePrice: "",
  });
  const [submittingAction, setSubmittingAction] = useState(false);

  const timerRef = useRef(null);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("admin");
    window.location.href = "/login";
  };

  // Fetch all in-flight live bookings
  const fetchLiveBookings = useCallback(async (isBackground = false) => {
    if (!isBackground) {
      setRefreshing(true);
    }

    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${BASE_URL}admin/bookings?status=LIVE&limit=100`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const json = await res.json();
      if (json.status === "success") {
        setLiveBookings(json.data.bookings || []);
      } else {
        if (!isBackground) {
          setError(json.message || "Failed to fetch live bookings");
        }
      }
    } catch (err) {
      console.error("Error fetching live bookings:", err);
      if (!isBackground) {
        setError("Network error fetching live bookings");
      }
    }

    setLoading(false);
    setRefreshing(false);
  }, []);

  // Initial load
  useEffect(() => {
    fetchLiveBookings();
  }, [fetchLiveBookings]);

  // Auto-polling interval (every 12 seconds)
  useEffect(() => {
    if (!autoSync) {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }

    timerRef.current = setInterval(() => {
      fetchLiveBookings(true);
    }, 12000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [autoSync, fetchLiveBookings]);

  // Clear toast notifications after 5 seconds
  useEffect(() => {
    if (error || success) {
      const t = setTimeout(() => {
        setError("");
        setSuccess("");
      }, 5000);
      return () => clearTimeout(t);
    }
  }, [error, success]);

  // Calculate Elapsed Duration
  const getElapsedString = (booking) => {
    let referenceTime;
    let prefix = "Active";

    if (booking.status === "STARTED" && booking.timestamps?.startedAt) {
      referenceTime = new Date(booking.timestamps.startedAt).getTime();
      prefix = "Working for";
    } else if (booking.status === "ACCEPTED" && booking.timestamps?.acceptedAt) {
      referenceTime = new Date(booking.timestamps.acceptedAt).getTime();
      prefix = "On the way";
    } else if (booking.timestamps?.createdAt) {
      referenceTime = new Date(booking.timestamps.createdAt).getTime();
      prefix = "Searching for";
    } else {
      return "Live Now";
    }

    const diffMinutes = Math.max(
      0,
      Math.floor((Date.now() - referenceTime) / (1000 * 60))
    );

    if (diffMinutes < 1) return `${prefix}: Just now`;
    if (diffMinutes < 60) return `${prefix}: ${diffMinutes}m`;
    const hours = Math.floor(diffMinutes / 60);
    const mins = diffMinutes % 60;
    return `${prefix}: ${hours}h ${mins}m`;
  };

  // KPI Metrics Calculation
  const kpis = useMemo(() => {
    const total = liveBookings.length;
    const searching = liveBookings.filter(
      (b) => b.status === "SEARCHING" || b.status === "PENDING"
    ).length;
    const accepted = liveBookings.filter((b) => b.status === "ACCEPTED").length;
    const started = liveBookings.filter((b) => b.status === "STARTED").length;

    return { total, searching, accepted, started };
  }, [liveBookings]);

  // Filtered Bookings
  const filteredBookings = useMemo(() => {
    let list = liveBookings;

    if (activeFilter === "SEARCHING") {
      list = list.filter(
        (b) => b.status === "SEARCHING" || b.status === "PENDING"
      );
    } else if (activeFilter === "ACCEPTED") {
      list = list.filter((b) => b.status === "ACCEPTED");
    } else if (activeFilter === "STARTED") {
      list = list.filter((b) => b.status === "STARTED");
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter(
        (b) =>
          (b.id || "").toLowerCase().includes(q) ||
          (b.user?.name || "").toLowerCase().includes(q) ||
          (b.user?.phone || "").toLowerCase().includes(q) ||
          (b.provider?.name || "").toLowerCase().includes(q) ||
          (b.provider?.phone || "").toLowerCase().includes(q) ||
          (b.service?.name || "").toLowerCase().includes(q) ||
          (b.location?.city || "").toLowerCase().includes(q)
      );
    }

    return list;
  }, [liveBookings, activeFilter, searchQuery]);

  // Execute Admin Override Action
  const handleExecuteAction = async () => {
    const { type, booking, reason, overridePrice } = actionModal;
    if (!booking) return;

    setSubmittingAction(true);
    setError("");

    try {
      const token = localStorage.getItem("token");
      let endpoint = "";
      let body = {};

      if (type === "FORCE_START") {
        endpoint = `${BASE_URL}admin/bookings/${booking.id}/force-start`;
        body = { adminNotes: reason || "Started by Admin via Live Console (OTP Bypassed)" };
      } else if (type === "FORCE_COMPLETE") {
        endpoint = `${BASE_URL}admin/bookings/${booking.id}/force-complete`;
        body = {
          adminNotes: reason || "Completed by Admin via Live Console",
          finalPrice: overridePrice ? parseFloat(overridePrice) : undefined,
        };
      } else if (type === "RE_SEARCH") {
        endpoint = `${BASE_URL}admin/bookings/${booking.id}/re-search`;
        body = { reason: reason || "Re-dispatched by admin to find alternate buddy" };
      } else if (type === "CANCEL") {
        endpoint = `${BASE_URL}admin/bookings/${booking.id}/cancel`;
        body = { reason: reason || "Cancelled by admin via Live Console" };
      }

      const res = await fetch(endpoint, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      const data = await res.json();

      if (data.status === "success") {
        setSuccess(
          data.message || `Action executed successfully on booking #${booking.id.slice(-8)}`
        );
        setActionModal({
          isOpen: false,
          type: null,
          booking: null,
          reason: "",
          overridePrice: "",
        });
        fetchLiveBookings();
      } else {
        setError(data.message || "Action failed to execute");
      }
    } catch (err) {
      console.error("Action execution error:", err);
      setError("Network error executing live action");
    }

    setSubmittingAction(false);
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] text-stone-900 flex flex-col font-sans">
      <Sidebar admin={admin} onLogout={handleLogout} />

      <main className="lg:ml-72 min-h-screen flex flex-col flex-1 pb-16">
        {/* Top Header */}
        <header className="bg-white border-b border-stone-200 px-4 sm:px-6 lg:px-8 py-5 sticky top-0 z-30 shadow-xs">
          <div className="pt-16 lg:pt-0 flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
            <div>
              {/* Breadcrumb */}
              <div className="flex items-center gap-2 text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">
                <span>Admin</span>
                <span>/</span>
                <span>Operations</span>
                <span>/</span>
                <span className="text-stone-900 font-bold">Live Radar</span>
              </div>
              <h1 className="text-2xl lg:text-3xl font-bold text-stone-900 tracking-tight flex items-center gap-3">
                <span>Live Bookings Control Center</span>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-stone-100 text-stone-800 border border-stone-200">
                  <span className="w-2 h-2 rounded-full bg-rose-600 shrink-0" />
                  <span>Real-Time Monitor</span>
                </span>
              </h1>
              <p className="text-sm text-stone-500 mt-1">
                Active supervision for in-flight bookings. Force-start services, bypass OTPs, complete jobs, or re-dispatch buddies.
              </p>
            </div>

            {/* Live Sync Controls */}
            <div className="flex flex-wrap items-center gap-3 self-start xl:self-auto">
              {/* Auto Sync Toggle */}
              <button
                onClick={() => setAutoSync(!autoSync)}
                className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-semibold border transition-all ${
                  autoSync
                    ? "bg-stone-50 border-stone-300 text-stone-800"
                    : "bg-stone-100 border-stone-200 text-stone-500"
                }`}
              >
                <span
                  className={`w-2 h-2 rounded-full ${
                    autoSync ? "bg-emerald-600" : "bg-stone-400"
                  }`}
                />
                <span>Auto-Sync: {autoSync ? "Active (12s)" : "Paused"}</span>
              </button>

              {/* Manual Refresh */}
              <button
                onClick={() => fetchLiveBookings(false)}
                disabled={refreshing}
                className="inline-flex items-center gap-2 px-4 py-2 bg-stone-900 hover:bg-stone-800 text-white rounded-xl text-xs font-semibold transition-all shadow-xs disabled:opacity-60"
              >
                <svg
                  className={`w-3.5 h-3.5 text-[#F8D346] ${refreshing ? "animate-spin" : ""}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                <span>{refreshing ? "Syncing..." : "Sync Radar"}</span>
              </button>
            </div>
          </div>
        </header>

        {/* Floating Notification Alerts */}
        <div className="px-4 sm:px-6 lg:px-8 pt-4">
          {error && (
            <div className="mb-4 p-4 rounded-xl bg-red-50 border border-red-200 text-red-800 text-sm font-medium flex items-center justify-between shadow-xs">
              <div className="flex items-center gap-3">
                <svg
                  className="w-5 h-5 text-red-600 shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>{error}</span>
              </div>
              <button
                onClick={() => setError("")}
                className="text-red-500 hover:text-red-700 text-sm font-semibold"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}

          {success && (
            <div className="mb-4 p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm font-medium flex items-center justify-between shadow-xs">
              <div className="flex items-center gap-3">
                <svg
                  className="w-5 h-5 text-emerald-600 shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>{success}</span>
              </div>
              <button
                onClick={() => setSuccess("")}
                className="text-emerald-500 hover:text-emerald-700 text-sm font-semibold"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}
        </div>

        {/* Main Content Area */}
        <div className="px-4 sm:px-6 lg:px-8 pt-2 space-y-6">
          {/* Live KPI Status Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Total In-Flight */}
            <div
              onClick={() => setActiveFilter("ALL")}
              className={`bg-white rounded-2xl border p-5 shadow-xs cursor-pointer transition-all ${
                activeFilter === "ALL"
                  ? "border-stone-900 ring-1 ring-stone-900/10"
                  : "border-stone-200 hover:border-stone-300"
              }`}
            >
              <div className="flex items-center justify-between text-stone-500 mb-2">
                <span className="text-xs font-bold uppercase tracking-wider">
                  Total In-Flight
                </span>
                <span className="w-2 h-2 rounded-full bg-stone-700" />
              </div>
              <div className="text-3xl font-extrabold text-stone-900">
                {kpis.total}
              </div>
              <div className="text-xs text-stone-500 mt-1">Active on platform</div>
            </div>

            {/* Searching */}
            <div
              onClick={() => setActiveFilter("SEARCHING")}
              className={`bg-white rounded-2xl border p-5 shadow-xs cursor-pointer transition-all ${
                activeFilter === "SEARCHING"
                  ? "border-amber-400 ring-1 ring-amber-400/20"
                  : "border-stone-200 hover:border-stone-300"
              }`}
            >
              <div className="flex items-center justify-between text-amber-800 mb-2">
                <span className="text-xs font-bold uppercase tracking-wider">
                  Searching Buddy
                </span>
                <span className="w-2 h-2 rounded-full bg-amber-500" />
              </div>
              <div className="text-3xl font-extrabold text-stone-900">
                {kpis.searching}
              </div>
              <div className="text-xs text-stone-500 mt-1">Waiting for acceptance</div>
            </div>

            {/* Buddy On The Way */}
            <div
              onClick={() => setActiveFilter("ACCEPTED")}
              className={`bg-white rounded-2xl border p-5 shadow-xs cursor-pointer transition-all ${
                activeFilter === "ACCEPTED"
                  ? "border-blue-400 ring-1 ring-blue-400/20"
                  : "border-stone-200 hover:border-stone-300"
              }`}
            >
              <div className="flex items-center justify-between text-blue-800 mb-2">
                <span className="text-xs font-bold uppercase tracking-wider">
                  On The Way
                </span>
                <span className="w-2 h-2 rounded-full bg-blue-500" />
              </div>
              <div className="text-3xl font-extrabold text-stone-900">
                {kpis.accepted}
              </div>
              <div className="text-xs text-stone-500 mt-1">Buddy en-route / arrived</div>
            </div>

            {/* Actively Working */}
            <div
              onClick={() => setActiveFilter("STARTED")}
              className={`bg-white rounded-2xl border p-5 shadow-xs cursor-pointer transition-all ${
                activeFilter === "STARTED"
                  ? "border-emerald-400 ring-1 ring-emerald-400/20"
                  : "border-stone-200 hover:border-stone-300"
              }`}
            >
              <div className="flex items-center justify-between text-emerald-800 mb-2">
                <span className="text-xs font-bold uppercase tracking-wider">
                  In-Progress
                </span>
                <span className="w-2 h-2 rounded-full bg-emerald-500" />
              </div>
              <div className="text-3xl font-extrabold text-stone-900">
                {kpis.started}
              </div>
              <div className="text-xs text-stone-500 mt-1">Service in progress</div>
            </div>
          </div>

          {/* Filter Bar & Search */}
          <div className="bg-white rounded-2xl border border-stone-200 p-4 shadow-xs flex flex-col sm:flex-row items-center justify-between gap-4">
            {/* Segmented Filter Tabs */}
            <div className="flex items-center gap-1.5 p-1 bg-stone-100 rounded-xl w-full sm:w-auto overflow-x-auto">
              <button
                onClick={() => setActiveFilter("ALL")}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all whitespace-nowrap ${
                  activeFilter === "ALL"
                    ? "bg-white text-stone-900 shadow-xs"
                    : "text-stone-600 hover:text-stone-900"
                }`}
              >
                All Live ({kpis.total})
              </button>
              <button
                onClick={() => setActiveFilter("SEARCHING")}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all whitespace-nowrap ${
                  activeFilter === "SEARCHING"
                    ? "bg-white text-stone-900 shadow-xs"
                    : "text-stone-600 hover:text-stone-900"
                }`}
              >
                Searching ({kpis.searching})
              </button>
              <button
                onClick={() => setActiveFilter("ACCEPTED")}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all whitespace-nowrap ${
                  activeFilter === "ACCEPTED"
                    ? "bg-white text-stone-900 shadow-xs"
                    : "text-stone-600 hover:text-stone-900"
                }`}
              >
                On The Way ({kpis.accepted})
              </button>
              <button
                onClick={() => setActiveFilter("STARTED")}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all whitespace-nowrap ${
                  activeFilter === "STARTED"
                    ? "bg-white text-stone-900 shadow-xs"
                    : "text-stone-600 hover:text-stone-900"
                }`}
              >
                In-Progress ({kpis.started})
              </button>
            </div>

            {/* Search Input */}
            <div className="relative w-full sm:w-72">
              <svg
                className="w-4 h-4 text-stone-400 absolute left-3.5 top-1/2 -translate-y-1/2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input
                type="text"
                placeholder="Search user, buddy, ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-8 py-2 bg-stone-50 border border-stone-300 rounded-xl text-xs text-stone-900 focus:outline-none focus:ring-2 focus:ring-[#F8D346]"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-700 text-xs font-bold"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Live Bookings Cards Stream */}
          {loading ? (
            <div className="bg-white rounded-2xl border border-stone-200 p-16 text-center shadow-xs flex flex-col items-center justify-center">
              <div className="w-10 h-10 border-3 border-stone-200 border-t-[#1C1917] rounded-full animate-spin mb-4"></div>
              <p className="text-sm font-semibold text-stone-700">
                Scanning Live Operational Radar...
              </p>
            </div>
          ) : filteredBookings.length === 0 ? (
            <div className="bg-white rounded-2xl border border-stone-200 p-16 text-center shadow-xs">
              <div className="w-12 h-12 rounded-xl bg-stone-100 text-stone-500 flex items-center justify-center mx-auto mb-4 border border-stone-200">
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h3 className="text-base font-bold text-stone-900">
                No Active Live Bookings
              </h3>
              <p className="text-xs text-stone-500 mt-1 max-w-md mx-auto">
                {searchQuery
                  ? `No live in-flight bookings match "${searchQuery}". Clear your search.`
                  : "All bookings are currently fulfilled, completed, or cancelled. New bookings will automatically stream into this radar."}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredBookings.map((b) => {
                const isSearching = b.status === "SEARCHING" || b.status === "PENDING";
                const isAccepted = b.status === "ACCEPTED";
                const isStarted = b.status === "STARTED";

                return (
                  <div
                    key={b.id}
                    className="bg-white rounded-2xl border border-stone-200 shadow-xs overflow-hidden"
                  >
                    {/* Card Top Banner */}
                    <div className="px-5 py-3.5 border-b border-stone-100 bg-stone-50/70 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="flex flex-wrap items-center gap-3">
                        {/* Live Status Badge */}
                        <div
                          className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold border ${
                            isStarted
                              ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                              : isAccepted
                              ? "bg-blue-50 text-blue-800 border-blue-200"
                              : "bg-amber-50 text-amber-800 border-amber-200"
                          }`}
                        >
                          <span
                            className={`w-2 h-2 rounded-full ${
                              isStarted
                                ? "bg-emerald-600"
                                : isAccepted
                                ? "bg-blue-600"
                                : "bg-amber-500"
                            }`}
                          />
                          <span>
                            {isStarted
                              ? "In-Progress (Working)"
                              : isAccepted
                              ? "Buddy Assigned (En-Route)"
                              : "Broadcasting Search"}
                          </span>
                        </div>

                        {/* Live Elapsed Counter */}
                        <div className="flex items-center gap-1.5 text-xs text-stone-600 bg-white px-2.5 py-1 rounded-lg border border-stone-200 font-medium">
                          <svg
                            className="w-3.5 h-3.5 text-stone-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                          <span>{getElapsedString(b)}</span>
                        </div>

                        <span className="font-mono text-xs text-stone-400">
                          #{b.id}
                        </span>
                      </div>

                      {/* Scheduled Slot Badge */}
                      <div className="text-xs font-medium text-stone-600 bg-white px-3 py-1 rounded-lg border border-stone-200 self-start sm:self-auto">
                        Slot: {b.scheduledDate || "Today"} • {b.scheduledTime || "Now"}
                      </div>
                    </div>

                    {/* Card Body - 3 Columns Layout */}
                    <div className="p-5 grid grid-cols-1 lg:grid-cols-12 gap-5">
                      {/* Col 1: Customer & Address (5 cols) */}
                      <div className="lg:col-span-5 space-y-3">
                        <div>
                          <span className="text-[10px] font-bold uppercase tracking-wider text-stone-400 block">
                            Service & Trade
                          </span>
                          <div className="text-sm font-bold text-stone-900 mt-0.5">
                            {b.service?.name || "General Service"}{" "}
                            <span className="text-stone-400 font-normal">/</span>{" "}
                            <span className="text-stone-700 font-semibold">
                              {b.subService?.name || "Standard"}
                            </span>
                          </div>
                        </div>

                        <div>
                          <span className="text-[10px] font-bold uppercase tracking-wider text-stone-400 block">
                            Client Details
                          </span>
                          <div className="flex items-center gap-2.5 mt-0.5">
                            <span className="font-bold text-sm text-stone-900">
                              {b.user?.name || "Client"}
                            </span>
                            {b.user?.phone && (
                              <a
                                href={`tel:${b.user.phone}`}
                                className="inline-flex items-center gap-1.5 text-xs font-semibold text-stone-700 hover:text-stone-950 bg-stone-100 hover:bg-stone-200 px-2.5 py-1 rounded-lg transition-colors"
                              >
                                <svg className="w-3 h-3 text-stone-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                </svg>
                                <span>{b.user.phone}</span>
                              </a>
                            )}
                          </div>
                        </div>

                        <div>
                          <span className="text-[10px] font-bold uppercase tracking-wider text-stone-400 block">
                            Service Location
                          </span>
                          <p className="text-xs text-stone-700 mt-0.5 leading-relaxed">
                            {b.location?.address || "Address not provided"}
                            {b.location?.city && `, ${b.location.city}`}
                            {b.location?.pincode && ` - ${b.location.pincode}`}
                          </p>
                          {b.location?.address && (
                            <a
                              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                                b.location.address
                              )}`}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-600 hover:underline mt-1"
                            >
                              <span>View on Google Maps</span>
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                              </svg>
                            </a>
                          )}
                        </div>
                      </div>

                      {/* Col 2: Provider / Buddy Status (4 cols) */}
                      <div className="lg:col-span-4 border-t lg:border-t-0 lg:border-l border-stone-200 pt-4 lg:pt-0 lg:pl-5 space-y-3">
                        <div>
                          <span className="text-[10px] font-bold uppercase tracking-wider text-stone-400 block">
                            Assigned Buddy / Worker
                          </span>
                          {b.provider?.id ? (
                            <div className="mt-1 space-y-1.5">
                              <div className="flex items-center gap-2">
                                <span className="font-bold text-sm text-stone-900">
                                  {b.provider.name}
                                </span>
                                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-stone-100 text-stone-700">
                                  {b.provider.status || "ACTIVE"}
                                </span>
                              </div>
                              {b.provider.phone && (
                                <a
                                  href={`tel:${b.provider.phone}`}
                                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-stone-700 hover:text-stone-950 bg-stone-100 hover:bg-stone-200 px-2.5 py-1 rounded-lg transition-colors"
                                >
                                  <svg className="w-3 h-3 text-stone-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                  </svg>
                                  <span>{b.provider.phone}</span>
                                </a>
                              )}
                            </div>
                          ) : (
                            <div className="mt-1 p-2.5 rounded-xl bg-stone-50 border border-stone-200 text-stone-700 text-xs">
                              <div className="font-semibold text-stone-900">Broadcasting Job Offer</div>
                              <div className="text-[11px] text-stone-500 mt-0.5">
                                Alert sent to verified buddies in {b.location?.city || "service area"}.
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Handshake OTP Display Box */}
                        <div className="p-3 rounded-xl bg-stone-50 border border-stone-200">
                          <div className="flex items-center justify-between">
                            <span className="text-[11px] font-bold uppercase tracking-wider text-stone-600">
                              Service Handshake OTP
                            </span>
                            <span className="text-xs font-bold font-mono text-stone-900 bg-white px-2 py-0.5 rounded border border-stone-300">
                              {b.serviceOtp || "N/A"}
                            </span>
                          </div>
                          <p className="text-[10px] text-stone-500 mt-1">
                            {isStarted
                              ? "Handshake verified & consumed"
                              : "Verify with customer, or use admin bypass below"}
                          </p>
                        </div>
                      </div>

                      {/* Col 3: Admin Super-Powers / Actions (3 cols) */}
                      <div className="lg:col-span-3 border-t lg:border-t-0 lg:border-l border-stone-200 pt-4 lg:pt-0 lg:pl-5 flex flex-col justify-center gap-2">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-stone-400 block mb-1">
                          Admin Operations
                        </span>

                        {/* Force Start Button (Active on ACCEPTED / PENDING) */}
                        {(isAccepted || b.status === "PENDING") && (
                          <button
                            onClick={() =>
                              setActionModal({
                                isOpen: true,
                                type: "FORCE_START",
                                booking: b,
                                reason: "",
                                overridePrice: "",
                              })
                            }
                            className="w-full py-2.5 px-3 rounded-xl bg-[#F8D346] hover:bg-[#edd03e] text-[#1C1917] text-xs font-bold transition-all shadow-xs flex items-center justify-center gap-1.5"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span>Force Start (Bypass OTP)</span>
                          </button>
                        )}

                        {/* Force Complete Button (Active on STARTED) */}
                        {isStarted && (
                          <button
                            onClick={() =>
                              setActionModal({
                                isOpen: true,
                                type: "FORCE_COMPLETE",
                                booking: b,
                                reason: "",
                                overridePrice: "",
                              })
                            }
                            className="w-full py-2.5 px-3 rounded-xl bg-stone-900 hover:bg-stone-800 text-white text-xs font-bold transition-all shadow-xs flex items-center justify-center gap-1.5"
                          >
                            <svg className="w-3.5 h-3.5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                            </svg>
                            <span>Mark Job Completed</span>
                          </button>
                        )}

                        {/* Re-Dispatch / Re-Search (Active on ACCEPTED if buddy stuck) */}
                        {isAccepted && (
                          <button
                            onClick={() =>
                              setActionModal({
                                isOpen: true,
                                type: "RE_SEARCH",
                                booking: b,
                                reason: "",
                                overridePrice: "",
                              })
                            }
                            className="w-full py-2 px-3 rounded-xl bg-white hover:bg-stone-100 text-stone-700 text-xs font-semibold transition-all border border-stone-300 flex items-center justify-center gap-1.5"
                          >
                            <svg className="w-3.5 h-3.5 text-stone-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            <span>Re-dispatch Buddy</span>
                          </button>
                        )}

                        {/* Cancel Booking */}
                        <button
                          onClick={() =>
                            setActionModal({
                              isOpen: true,
                              type: "CANCEL",
                              booking: b,
                              reason: "",
                              overridePrice: "",
                            })
                          }
                          className="w-full py-2 px-3 rounded-xl bg-white hover:bg-rose-50 text-rose-700 text-xs font-semibold transition-all border border-rose-200 flex items-center justify-center gap-1.5"
                        >
                          <svg className="w-3.5 h-3.5 text-rose-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                          <span>Cancel Booking</span>
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>

      {/* Action Execution Modal */}
      {actionModal.isOpen && actionModal.booking && (
        <div className="fixed inset-0 bg-stone-900/50 backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full border border-stone-200 overflow-hidden">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-stone-200 bg-stone-50 flex items-center justify-between">
              <div>
                <h3 className="text-base font-bold text-stone-900">
                  {actionModal.type === "FORCE_START" && "Force Start Service (Bypass OTP)"}
                  {actionModal.type === "FORCE_COMPLETE" && "Force Complete Service"}
                  {actionModal.type === "RE_SEARCH" && "Re-dispatch to Alternate Buddy"}
                  {actionModal.type === "CANCEL" && "Cancel In-Flight Booking"}
                </h3>
                <p className="text-xs text-stone-500 mt-0.5 font-mono">
                  Booking #{actionModal.booking.id}
                </p>
              </div>
              <button
                onClick={() => setActionModal({ isOpen: false, type: null, booking: null })}
                className="w-8 h-8 rounded-lg bg-stone-100 hover:bg-stone-200 text-stone-500 flex items-center justify-center transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-4 text-xs">
              {actionModal.type === "FORCE_START" && (
                <div className="p-3.5 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 space-y-1">
                  <div className="font-semibold">Customer Handshake Bypass</div>
                  <p className="text-[11px] text-amber-800 leading-relaxed">
                    This starts the service clock, sets status to <strong>STARTED</strong>, and notifies the buddy to begin work without requiring the customer's OTP.
                  </p>
                </div>
              )}

              {actionModal.type === "FORCE_COMPLETE" && (
                <div className="p-3.5 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-900 space-y-1">
                  <div className="font-semibold">Job Completion & Settlement</div>
                  <p className="text-[11px] text-emerald-800 leading-relaxed">
                    This marks the service <strong>COMPLETED</strong>, calculates commission, releases buddy payout, and frees the buddy for new assignments.
                  </p>
                </div>
              )}

              {actionModal.type === "RE_SEARCH" && (
                <div className="p-3.5 rounded-xl bg-blue-50 border border-blue-200 text-blue-900 space-y-1">
                  <div className="font-semibold">Buddy Re-dispatch</div>
                  <p className="text-[11px] text-blue-800 leading-relaxed">
                    Current buddy <strong>{actionModal.booking.provider?.name || "assigned"}</strong> will be unassigned, and the job will return to <strong>SEARCHING</strong> broadcast.
                  </p>
                </div>
              )}

              {actionModal.type === "CANCEL" && (
                <div className="p-3.5 rounded-xl bg-rose-50 border border-rose-200 text-rose-900 space-y-1">
                  <div className="font-semibold">Immediate Cancellation</div>
                  <p className="text-[11px] text-rose-800 leading-relaxed">
                    This will terminate the in-flight booking. Both client and buddy will receive real-time cancellation alerts.
                  </p>
                </div>
              )}

              {/* Admin Note / Reason */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-stone-700 mb-1.5">
                  Admin Log Note / Reason *
                </label>
                <textarea
                  rows={2}
                  required
                  value={actionModal.reason}
                  onChange={(e) =>
                    setActionModal((prev) => ({ ...prev, reason: e.target.value }))
                  }
                  placeholder="e.g. Customer unreachable by phone / Emergency dispatch"
                  className="w-full px-3 py-2 bg-stone-50 border border-stone-300 rounded-xl text-xs text-stone-900 focus:outline-none focus:ring-2 focus:ring-[#F8D346]"
                />
              </div>

              {/* Action Buttons */}
              <div className="pt-2 flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setActionModal({ isOpen: false, type: null, booking: null })}
                  className="flex-1 py-2.5 px-4 rounded-xl border border-stone-300 hover:bg-stone-100 text-stone-700 font-semibold transition-all"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={submittingAction}
                  onClick={handleExecuteAction}
                  className={`flex-1 py-2.5 px-4 rounded-xl font-bold shadow-xs transition-all flex items-center justify-center gap-2 ${
                    actionModal.type === "CANCEL"
                      ? "bg-rose-600 hover:bg-rose-700 text-white"
                      : actionModal.type === "FORCE_COMPLETE"
                      ? "bg-stone-900 hover:bg-stone-800 text-white"
                      : "bg-[#F8D346] hover:bg-[#edd03e] text-[#1C1917]"
                  }`}
                >
                  {submittingAction && (
                    <div className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                  )}
                  <span>Confirm & Execute</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LiveBookings;
